'use client';

import { createContext, useContext, useSyncExternalStore } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { PostHog_ClientFeaturesSDK } from '~/modules/posthog/features/client';
import { _POSTHOG_SHARED_CONFIG } from '~/modules/posthog/shared';
import { usePortalCtx } from '~/features/global/portal';
import { resolvePlpVariant } from './shared';

/**
 * The one flag behind every visitor-facing change in the PLP and filter
 * finetuning project: the filter restructure, the results header, the activity
 * cards, the mobile layout, the USP block and the promotion card.
 *
 * One flag rather than one per ticket, because the changes are small on their
 * own and separate tests would each take too long to reach significance. That
 * also means a visitor sees either all of it or none of it, never a half-built
 * page.
 *
 * Two things ship OUTSIDE this flag, to both groups: the `plp.filter.change`
 * event and the AI card description with its admin field. Tracking has to run
 * on the control group for the two to be comparable, and content is not a
 * layout change.
 */
export const PLP_EXPERIMENT_FLAG = 'plp-finetuning';

/** Forces a variant regardless of the flag: `?plp=finetuning` or `?plp=control`.
 * PostHog buckets each visitor at random, which is right for the experiment but
 * useless for reviewing a Vercel preview, where you need to open the same URL
 * twice and compare. Only these two exact values do anything; anything else
 * falls through to the flag. */
export const PLP_OVERRIDE_PARAM = 'plp';

/** What the server already worked out for this visitor.
 *
 * The browser client answers `null` until PostHog has loaded, so without this a
 * bucketed visitor got the control page painted first and the layout swapped a
 * second later. The page resolves the flag server-side from the visitor cookie
 * and hands the answer down here, so the first paint is already the right one.
 * It only holds the page until the client answers; see `resolvePlpVariant`. */
const PlpExperimentContext = createContext<'finetuning' | 'control' | null>(null);

export const PlpExperimentProvider: React.FC<{
    variant: 'finetuning' | 'control' | null;
    children: React.ReactNode;
}> = props => (
    <PlpExperimentContext.Provider value={props.variant}>
        {props.children}
    </PlpExperimentContext.Provider>
);

/**
 * Whether this visitor sees the reworked PLP.
 *
 * Client components only. `usePostHog` needs the browser client, so a server
 * component reads the flag through `PostHog_FeaturesSDK.getFlag` with
 * `PLP_EXPERIMENT_FLAG` instead. Shows the server's answer until PostHog has
 * loaded, then PostHog's, and re-renders when the flags arrive so every
 * component switches together.
 */
export function usePlpExperiment(): boolean {
    const posthog = usePostHog();
    const override = useSearchParams().get(PLP_OVERRIDE_PARAM);
    const portal = usePortalCtx();
    const serverVariant = useContext(PlpExperimentContext);
    const clientVariant = useSyncExternalStore(
        onFlagsChange => posthog.onFeatureFlags(onFlagsChange),
        () => PostHog_ClientFeaturesSDK.getFlag(posthog, PLP_EXPERIMENT_FLAG),
        () => null,
    );

    // The experiment is a public-website test. The BIZZ and partner portals
    // reuse the search components and keep today's layout, whatever the flag.
    if (portal?.portal !== 'WEB') return false;

    return (
        resolvePlpVariant({
            override,
            clientOverride: PostHog_ClientFeaturesSDK.getOverride(posthog, PLP_EXPERIMENT_FLAG),
            server: serverVariant,
            client: clientVariant,
            // Previews default to the reworked page so it can be reviewed without
            // a URL parameter; production stays on today's page.
            fallback: _POSTHOG_SHARED_CONFIG.ENVIRONMENT === 'preview' ? 'finetuning' : 'control',
        }) === 'finetuning'
    );
}
